<template>
  <Listbox
    :name="name"
    as="div"
    :modelValue="modelValue"
    @update:modelValue="onSelect"
    :multiple="multiple"
    :disabled="disabled"
    class="relative"
  >
    <ListboxButton
      ref="dropdownInputButton"
      :class="{
        '!cursor-not-allowed opacity-70': disabled,
        'hover:border-gray-400 text-base focus:border-gray-500 focus:outline-none focus:ring-gray-500':
          !disabled,
      }"
      class="flex items-center whitespace-nowrap rounded-sm border-gray-300 border py-2 px-3 transition sm:text-sm"
    >
      <span
        v-if="selectedItems.length === 0"
        class="flex items-center text-gray-500"
      >
        {{ t("components.inputs.dropdownInput.placeholder") }}
      </span>
      <span v-else class="flex items-center">
        {{ formattedSelectedItems }}
      </span>
      <span class="ml-2">
        <ChevronDownIcon
          v-if="selectedItems.length === 0 || !multiple || !allowEmpty"
          class="h-5 w-5 text-gray-900"
          aria-hidden="true"
        />
        <XMarkIcon
          v-else
          @click.prevent="clear"
          class="h-5 w-5 text-gray-900"
          aria-hidden="true"
        />
      </span>
    </ListboxButton>

    <transition
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <ListboxOptions
        :class="{
          'bottom-full mt-0 mb-3': shouldRenderOnTop,
        }"
        class="absolute left-0 sm:left-auto sm:w-auto z-10 mt-3 max-h-56 overflow-auto rounded-sm bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-sm"
      >
        <ListboxOption
          as="template"
          v-for="item in items"
          :key="item.value"
          :value="item.value"
          v-slot="{ active, selected }"
        >
          <li
            :class="[
              active ? 'text-gray-900 bg-gray-100' : 'text-gray-900',
              selected ? 'bg-gray-50' : '',
              'relative cursor-pointer select-none py-2 pl-3 pr-9',
            ]"
          >
            <div class="flex items-center space-x-2">
              <input
                v-if="multiple"
                class="h-4 w-4 rounded-xs border-gray-300 text-brand-900 focus:outline-0 focus:ring-offset-0 focus:ring-0"
                type="checkbox"
                :checked="selected"
                :disabled="
                  selectedItems.length === 1 && selected && !allowEmpty
                "
              />
              <span :class="['font-normal', selected && 'font-semibold']">
                {{ item.label }}
              </span>
            </div>
          </li>
        </ListboxOption>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref } from "vue";
import { XMarkIcon, ChevronDownIcon } from "@heroicons/vue/20/solid";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/vue";

import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  items: {
    type: Array,
    required: true,
  },
  modelValue: {
    // If multiple mode is enabled, we expect an array, otherwise a single item
    type: [Array, Object, String, Number],
    required: true,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  allowEmpty: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

const selectedItems = computed(() => {
  if (!Array.isArray(props.modelValue)) {
    return [
      props.items.find(
        (item) =>
          // Compare stringified values to avoid comparing objects
          JSON.stringify(item.value) === JSON.stringify(props.modelValue)
      ),
    ];
  }

  return props.items.filter((item) => props.modelValue.includes(item.value));
});

const formattedSelectedItems = computed(() => {
  if (selectedItems.value.length === 1) {
    return selectedItems.value[0]?.label;
  }

  return selectedItems.value
    .map((item) => item.label.substring(0, 3))
    .join(", ");
});

const dropdownInputButton = ref(null);

const shouldRenderOnTop = computed(() => {
  const element = dropdownInputButton.value?.$el;

  if (!element) {
    return false;
  }

  const spaceBelow =
    window.innerHeight - element.getBoundingClientRect()?.bottom;

  // Each list item element is 36px tall
  // The container is max 224px tall
  let minimumRequiredSpace = Math.min(props.items.length * 36, 224);

  return spaceBelow < minimumRequiredSpace;
});

const clear = () => emit("update:modelValue", []);

const onSelect = (value) => {
  if (!props.allowEmpty) {
    if (value.length === 0) {
      return;
    }
  }

  emit("update:modelValue", value);
};
</script>
